import { Box, Flex, Spinner, Text } from '@chakra-ui/react';

const BRAND_NAVY = '#2D426A';

const SKELETON_TILES: ReadonlyArray<{
  gridColumn: string;
  gridRow: string;
  durationMs: number;
}> = [
  { gridColumn: '1 / span 8', gridRow: '1 / span 1', durationMs: 1600 },
  { gridColumn: '1 / span 4', gridRow: '2 / span 3', durationMs: 1800 },
  { gridColumn: '5 / span 4', gridRow: '2 / span 3', durationMs: 2000 },
  { gridColumn: '1 / span 4', gridRow: '5 / span 3', durationMs: 2200 },
  { gridColumn: '5 / span 4', gridRow: '5 / span 3', durationMs: 2400 },
];

const DEFAULT_CREATE_MESSAGE = 'Building your custom page…';
const DEFAULT_UPDATE_MESSAGE = 'Updating your custom page…';

interface CustomDashGenerationOverlayProps {
  mode: 'create' | 'update';
  /** Status text mirrored from the chat (e.g. "Generating page…"). */
  message: string | null;
  /**
   * Whether the grid currently has charts on it. We only render the
   * full skeleton overlay for empty grids — an update on an existing
   * page shows a compact banner instead so the user can still see the
   * charts being modified.
   */
  hasExistingItems: boolean;
}

export const CustomDashGenerationOverlay = ({
  mode,
  message,
  hasExistingItems,
}: CustomDashGenerationOverlayProps) => {
  const defaultMessage =
    mode === 'update' ? DEFAULT_UPDATE_MESSAGE : DEFAULT_CREATE_MESSAGE;
  const statusText = message?.trim() || defaultMessage;

  if (hasExistingItems) {
    return (
      <Flex
        position="absolute"
        top="0.75rem"
        left="50%"
        transform="translateX(-50%)"
        zIndex={5}
        align="center"
        gap={2}
        px={3}
        py={2}
        borderRadius="full"
        bg="white"
        boxShadow="md"
        border="1px solid #E2E8F0"
        data-testid="custom-dash-generation-status"
        pointerEvents="none"
        role="status"
        aria-live="polite"
      >
        <Spinner size="sm" color={BRAND_NAVY} />
        <Text fontSize="13px" color={BRAND_NAVY} fontWeight={500}>
          {statusText}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      position="absolute"
      top={0}
      right={0}
      bottom={0}
      left={0}
      zIndex={4}
      direction="column"
      align="stretch"
      justify="stretch"
      backgroundColor="var(--chakra-colors-background-primary)"
      data-testid="custom-dash-generation-overlay"
      role="status"
      aria-live="polite"
    >
      <Box
        display="grid"
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        gap="1rem"
        padding="0.5rem"
        gridTemplateColumns="repeat(8, 1fr)"
        gridTemplateRows="repeat(7, minmax(0, 1fr))"
        sx={{
          '@keyframes customDashSkeletonPulse': {
            '0%': { opacity: 0.35 },
            '50%': { opacity: 0.75 },
            '100%': { opacity: 0.35 },
          },
        }}
      >
        {SKELETON_TILES.map((tile) => (
          <Box
            key={`${tile.gridColumn}-${tile.gridRow}`}
            gridColumn={tile.gridColumn}
            gridRow={tile.gridRow}
            backgroundColor="#FFFFFF"
            borderRadius="var(--chakra-radii-md)"
            border="1px solid #E8EDF5"
            sx={{
              animation: `customDashSkeletonPulse ${tile.durationMs}ms ease-in-out infinite`,
            }}
          />
        ))}
      </Box>
      <Flex
        position="relative"
        flex="1"
        align="center"
        justify="center"
        direction="column"
        gap={3}
      >
        <Flex
          align="center"
          gap={3}
          px={4}
          py={3}
          borderRadius="lg"
          bg="white"
          boxShadow="md"
          border="1px solid #E2E8F0"
        >
          <Spinner size="md" color={BRAND_NAVY} />
          <Box>
            <Text fontSize="14px" color={BRAND_NAVY} fontWeight={600}>
              {statusText}
            </Text>
            <Text fontSize="12px" color={`${BRAND_NAVY}99`}>
              The assistant is assembling charts for this page.
            </Text>
          </Box>
        </Flex>
      </Flex>
    </Flex>
  );
};
